/**
 * Display helpers for DVN Private / luxury concept pricing.
 * Never treat indicative group budgets as per-person rates.
 */

export const PRICING_KINDS = Object.freeze({
  REQUEST_QUOTE: 'request_quote',
  INDICATIVE_GROUP_BUDGET: 'indicative_group_budget',
  CONFIRMED_PER_PERSON: 'confirmed_per_person',
  CONFIRMED_GROUP_TOTAL: 'confirmed_group_total',
});

export function formatMoneyAmount(amount, currency = 'USD') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString('en-US')}`;
  }
}

/**
 * @returns {{ kind: string, headline: string, subline: string|null, unit: string, isQuote: boolean, cardValue: string, cardExtra: string, assumptions?: string|null }|null}
 */
export function resolvePrivateJourneyPriceDisplay(pkg = {}) {
  if (pkg.price_display?.headline) return pkg.price_display;

  const currency = pkg.currency || 'USD';
  const kind = String(pkg.pricing_kind || '').trim()
    || (pkg.is_private_journey ? PRICING_KINDS.INDICATIVE_GROUP_BUDGET : '');

  if (kind === PRICING_KINDS.REQUEST_QUOTE) {
    return {
      kind,
      headline: 'Bespoke pricing',
      subline: 'Request a private quote',
      unit: 'none',
      isQuote: false,
      cardValue: 'Bespoke',
      cardExtra: 'Request a private quote',
    };
  }

  if (kind === PRICING_KINDS.INDICATIVE_GROUP_BUDGET) {
    const min = formatMoneyAmount(pkg.budget_min, currency);
    const max = formatMoneyAmount(pkg.budget_max, currency);
    const range = min && max ? `${min}–${max}` : (min || max || pkg.illustrative_budget_label || 'On request');
    return {
      kind,
      headline: `Illustrative group budget: ${range} total`,
      subline: 'Not a quote',
      unit: 'per_group',
      isQuote: false,
      cardValue: range,
      cardExtra: 'total · illustrative',
      assumptions: pkg.pricing_assumptions || null,
    };
  }

  if (kind === PRICING_KINDS.CONFIRMED_GROUP_TOTAL && (pkg.budget_min != null || pkg.base_price != null)) {
    const amount = formatMoneyAmount(pkg.budget_min ?? pkg.base_price, currency);
    return {
      kind,
      headline: `From ${amount} for the group`,
      subline: 'Subject to agreed terms',
      unit: 'per_group',
      isQuote: true,
      cardValue: amount,
      cardExtra: 'group total',
    };
  }

  if (kind === PRICING_KINDS.CONFIRMED_PER_PERSON) {
    const amount = formatMoneyAmount(pkg.base_price, currency);
    return {
      kind,
      headline: `From ${amount} per person`,
      subline: 'Supplier-confirmed',
      unit: 'per_person',
      isQuote: true,
      cardValue: amount,
      cardExtra: 'per person',
    };
  }

  if (pkg.is_private_journey) {
    const min = formatMoneyAmount(pkg.budget_min ?? pkg.base_price, currency);
    const max = formatMoneyAmount(pkg.budget_max, currency);
    const range = min && max ? `${min}–${max}` : (min || pkg.illustrative_budget_label || 'On request');
    return {
      kind: PRICING_KINDS.INDICATIVE_GROUP_BUDGET,
      headline: `Illustrative group budget: ${range} total`,
      subline: 'Not a quote',
      unit: 'per_group',
      isQuote: false,
      cardValue: range,
      cardExtra: 'total · illustrative',
      assumptions: pkg.pricing_assumptions || null,
    };
  }

  return null;
}

export function isPrivateLuxuryPackage(pkg) {
  return Boolean(pkg?.is_private_journey || pkg?.experience_tier === 'private_luxury');
}
